import * as React from 'react'

import { ArrowLeft, EyeVisible } from '@factorialco/f0-react/icons/app'
import { EngagementSurveyDetailTabsDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const SurveyResourceHeader = React.lazy(
  () => import('modules/engagement_surveys/components/SurveyResourceHeader')
)

const SurveysListPage = React.lazy(() => import('modules/engagement_surveys/pages/SurveysListPage'))
const RootPage = React.lazy(() => import('modules/engagement_surveys/pages/RootPage'))
const SurveyOverviewPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyOverviewPage')
)
const SurveyEditQuestionnairePage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyEditQuestionnairePage')
)
const SurveyResponsesPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyResponsesPage')
)
const SurveySmartInsightsPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveySmartInsightsPage')
)

const smartSurveysRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.engagement.smart_surveys.path,
      element: <RootPage />,
      handle: {
        meta: () => {
          return {
            title: i18n.t('surveys.title'),
          }
        },
      },
      children: [
        {
          path: '',
          element: <SurveysListPage />,
        },
        {
          path: navigation.engagement.smart_surveys.survey.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: EngagementSurveyDetailTabsDocument,
                variables: {
                  surveyId: Number(params.surveyId),
                },
              }).map(({ data }) => {
                const survey = data?.surveys.survey
                const canCreateSurvey = data?.surveys._policies.surveysCreateSurvey.can ?? false

                if (!survey) {
                  return { title: '' }
                }

                return {
                  title: survey.form.title,
                  headerComponent: (
                    <SurveyResourceHeader survey={survey} canCreateSurvey={canCreateSurvey} />
                  ),
                  subNavigation: [
                    {
                      title: i18n.t('surveys.tabs.overview'),
                      href: navigation.engagement.smart_surveys.survey.fullPath({
                        surveyId: survey.id,
                      }),
                      index: true,
                      isVisible: true,
                    },
                    {
                      title: i18n.t('surveys.tabs.responses'),
                      href: navigation.engagement.smart_surveys.survey.responses.fullPath({
                        surveyId: survey.id,
                      }),
                      isVisible: true,
                    },
                    {
                      title: i18n.t('surveys.tabs.smart_insights'),
                      href: navigation.engagement.smart_surveys.survey.smartInsights.fullPath({
                        surveyId: survey.id,
                      }),
                      isVisible: false,
                    },
                  ],
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <SurveyOverviewPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.responses.path,
              element: <SurveyResponsesPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.smartInsights.path,
              element: <SurveySmartInsightsPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.edit.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, navigate }) => {
                  const surveyId = Number(params.surveyId)

                  return watchQuery({
                    query: EngagementSurveyDetailTabsDocument,
                    variables: { surveyId },
                  }).map(({ data }) => {
                    const survey = data?.surveys.survey

                    if (!survey) {
                      return { title: '' }
                    }

                    const canEditSurvey = survey._policies?.edit.can

                    if (!canEditSurvey) {
                      navigate(navigation.engagement.smart_surveys.survey.fullPath({ surveyId }))
                      return { title: '' }
                    }

                    return {
                      title: i18n.t('surveys.edit_mode.title'),
                      headerComponentProps: {
                        title: survey.form.title,
                        primaryAction: {
                          key: 'save',
                          label: i18n.t('common.save'),
                        },
                        secondaryActions: [
                          {
                            key: 'back',
                            label: i18n.t('surveys.header.actions.back_to_overview'),
                            icon: ArrowLeft,
                          },
                          {
                            key: 'preview',
                            label: i18n.t('common.preview'),
                            icon: EyeVisible,
                          },
                        ],
                      },
                    }
                  })
                },
              },
              element: <SurveyEditQuestionnairePage />,
            },
          ],
        },
      ],
    },
  ]
}

export default smartSurveysRoutes
